'use client';

import type { PropsWithChildren } from 'react';
import React, { useRef, useState } from 'react';
import type { TooltipProps } from 'components/Tooltip/Tooltip';
import { Tooltip } from 'components/Tooltip/Tooltip';
import type { TextTruncation } from './types';
import { isMiddleTruncation } from './utils';

type TextWithTooltipProps = Partial<
  Pick<TooltipProps, 'title' | 'shouldRender'>
> &
  PropsWithChildren<{ truncate: TextTruncation }>;

/**
 * Render a tooltip when the text is truncated and the cursor
 * is hovered over the text. This supports all forms of truncation
 * supported by the Text component. This behavior should always
 * be opt-in, because it comes with a minor performance overhead.
 */

export const TextWithTooltip = (props: TextWithTooltipProps) => {
  const { children, truncate, title: titleProp, shouldRender } = props;

  const [showing, setShowing] = useState(false);
  const ref = useRef(null);

  function onTooltipRequestChange(isShowing: boolean) {
    if (isShowing) {
      // for single line, measure the text width
      if (truncate === true || isMiddleTruncation(truncate)) {
        setShowing(ref.current.scrollWidth > ref.current.clientWidth);
        return;
      }

      // for multi line truncation, measure height
      setShowing(ref.current.scrollHeight > ref.current.clientHeight);
      return;
    }

    setShowing(false);
  }

  const tooltipContent =
    titleProp ??
    React.cloneElement(children as any, {
      truncate: false,
      variant: 'secondary',
    });

  return (
    <Tooltip
      isShowing={showing}
      onRequestChange={onTooltipRequestChange}
      shouldRender={shouldRender}
      title={tooltipContent}
    >
      {React.cloneElement(children as any, { ref })}
    </Tooltip>
  );
};
